import { useState, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
    Download as DownloadIcon,
    Loader2,
    CheckCircle2,
    XCircle,
    Smartphone,
    KeyRound,
    FileDown,
    Sparkles,
    AlertTriangle,
    Hash,
} from "lucide-react";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.filescad.app";
const PLAY_BADGE = "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";
const DOWNLOAD_API = "https://download.filescad.com";

const STATUS = {
    IDLE: "idle",
    DOWNLOADING: "downloading",
    SUCCESS: "success",
    FAILED: "failed",
    INVALID: "invalid",
};

export default function Download() {
    const [pin, setPin] = useState("");
    const [status, setStatus] = useState(STATUS.IDLE);
    const [errorMsg, setErrorMsg] = useState("");
    const inputRef = useRef(null);

    const isValidPin = /^\d{6}$/.test(pin);

    const handleDownload = async () => {
        if (!isValidPin) {
            setStatus(STATUS.INVALID);
            setErrorMsg("Please enter a valid 6-digit PIN.");
            return;
        }
        setStatus(STATUS.DOWNLOADING);
        setErrorMsg("");
        try {
            const response = await fetch(`${DOWNLOAD_API}?pin=${pin}`);
            if (!response.ok) throw new Error("Network response was not ok");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = "filescad-design.zip";
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setStatus(STATUS.SUCCESS);
        } catch {
            setStatus(STATUS.FAILED);
            setErrorMsg("Failed to download. Check the PIN and try again.");
        }
    };

    const reset = () => {
        setPin("");
        setStatus(STATUS.IDLE);
        setErrorMsg("");
        setTimeout(() => inputRef.current?.focus(), 50);
    };

    const onPinChange = (val) => {
        setPin(val);
        if (status === STATUS.INVALID || status === STATUS.FAILED) {
            setStatus(STATUS.IDLE);
            setErrorMsg("");
        }
    };

    const steps = [
        { icon: Smartphone, title: "Open the FilesCAD app", desc: "Install FilesCAD on your Android device and sign in." },
        { icon: Hash, title: "Find your 6-digit PIN", desc: "Each purchased design has a unique PIN inside your library." },
        { icon: KeyRound, title: "Enter the PIN here", desc: "Type it into the box on this page." },
        { icon: FileDown, title: "Download instantly", desc: "Your CNC file (.zip) saves to your computer in seconds." },
    ];

    return (
        <div data-testid="download-page" className="min-h-screen bg-[#FAFAFA]">
            <Header />

            <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
                <div className="absolute inset-0 bg-blueprint pointer-events-none" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
                <div className="absolute top-40 -left-20 w-[400px] h-[400px] rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />

                <div className="relative max-w-6xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                        {/* LEFT — Headline & guide */}
                        <div className="lg:col-span-6">
                            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 shadow-sm">
                                <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                                <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-700">
                                    Download Portal
                                </span>
                            </div>

                            <h1 className="mt-8 font-display font-black tracking-tighter text-slate-950 text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
                                Download
                                <br />
                                by <span className="text-blue-600">PIN.</span>
                            </h1>

                            <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-lg">
                                Got a 6-digit PIN from the FilesCAD app? Enter it on the right and your CNC design file downloads instantly — no login, no friction.
                            </p>

                            {/* Step guide */}
                            <div className="mt-12 space-y-4">
                                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                                    How it works
                                </h3>
                                {steps.map((s, i) => (
                                    <div
                                        key={s.title}
                                        data-testid={`download-step-${i}`}
                                        className="flex items-start gap-4 p-4 rounded-2xl bg-white/60 backdrop-blur border border-slate-200/60"
                                    >
                                        <div className="shrink-0 w-10 h-10 rounded-xl bg-slate-950 grid place-items-center">
                                            <s.icon className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-baseline gap-2">
                                                <span className="font-mono text-xs text-blue-600">
                                                    {String(i + 1).padStart(2, "0")}
                                                </span>
                                                <h4 className="font-display font-bold text-slate-950">
                                                    {s.title}
                                                </h4>
                                            </div>
                                            <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10 flex items-center gap-4">
                                <span className="text-sm text-slate-500">Don&rsquo;t have the app yet?</span>
                                <a
                                    href={PLAY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="download-play-badge"
                                    className="hover:opacity-90 transition-opacity"
                                >
                                    <img src={PLAY_BADGE} alt="Get it on Google Play" className="h-12" />
                                </a>
                            </div>
                        </div>

                        {/* RIGHT — PIN card */}
                        <div className="lg:col-span-6 lg:sticky lg:top-28">
                            <div className="relative">
                                <div className="absolute -inset-6 bg-gradient-to-br from-blue-500/20 to-amber-400/20 rounded-[3rem] blur-2xl" />
                                <div
                                    data-testid="download-card"
                                    className="relative bg-white rounded-[2rem] border border-slate-200 shadow-2xl shadow-slate-900/10 p-8 md:p-12"
                                >
                                    {/* Status badge */}
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="flex items-center gap-3">
                                            <div className="w-11 h-11 rounded-2xl bg-slate-950 grid place-items-center">
                                                <KeyRound className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="font-display font-black text-xl text-slate-950 leading-none">
                                                    {status === STATUS.SUCCESS
                                                        ? "Downloaded"
                                                        : status === STATUS.DOWNLOADING
                                                            ? "Downloading…"
                                                            : status === STATUS.FAILED
                                                                ? "Failed"
                                                                : "Enter your PIN"}
                                                </h2>
                                                <p className="text-xs text-slate-500 mt-1 font-mono">
                                                    {status === STATUS.SUCCESS
                                                        ? "filescad-design.zip"
                                                        : status === STATUS.DOWNLOADING
                                                            ? "fetching design file…"
                                                            : "6-digit code from the app"}
                                                </p>
                                            </div>
                                        </div>

                                        <StatusPill status={status} />
                                    </div>

                                    {/* OTP input */}
                                    <div className="my-10 flex justify-center" data-testid="pin-input-wrapper">
                                        <InputOTP
                                            ref={inputRef}
                                            maxLength={6}
                                            value={pin}
                                            onChange={onPinChange}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            disabled={status === STATUS.DOWNLOADING || status === STATUS.SUCCESS}
                                            containerClassName="gap-2 md:gap-3"
                                            data-testid="pin-input"
                                        >
                                            <InputOTPGroup className="gap-2 md:gap-3">
                                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                                    <InputOTPSlot
                                                        key={i}
                                                        index={i}
                                                        data-testid={`pin-slot-${i}`}
                                                        className="!w-12 !h-16 md:!w-14 md:!h-20 !rounded-2xl !border-2 !border-slate-200 !bg-slate-50 first:!border-l-2 !text-2xl md:!text-3xl !font-display !font-black !text-slate-950 data-[active=true]:!border-blue-600 data-[active=true]:!bg-white data-[active=true]:!ring-4 data-[active=true]:!ring-blue-500/15"
                                                    />
                                                ))}
                                            </InputOTPGroup>
                                        </InputOTP>
                                    </div>

                                    {/* Error message */}
                                    {(status === STATUS.INVALID || status === STATUS.FAILED) && (
                                        <div
                                            data-testid="download-error"
                                            className="mb-6 flex items-start gap-3 rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700"
                                        >
                                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                            <span>{errorMsg}</span>
                                        </div>
                                    )}

                                    {/* Success message */}
                                    {status === STATUS.SUCCESS && (
                                        <div
                                            data-testid="download-success"
                                            className="mb-6 flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700"
                                        >
                                            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                                            <span>
                                                Your download has started. If nothing happened, click the button again or check your browser&rsquo;s download bar.
                                            </span>
                                        </div>
                                    )}

                                    {/* Action button */}
                                    {status !== STATUS.SUCCESS ? (
                                        <button
                                            type="button"
                                            data-testid="download-submit-btn"
                                            disabled={!isValidPin || status === STATUS.DOWNLOADING}
                                            onClick={handleDownload}
                                            className="group relative w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-slate-950 hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-6 py-5 text-base font-bold transition-all hover:-translate-y-0.5 disabled:hover:translate-y-0"
                                        >
                                            {status === STATUS.DOWNLOADING ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    Downloading…
                                                </>
                                            ) : (
                                                <>
                                                    <DownloadIcon className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />
                                                    Download Design File
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid="download-reset-btn"
                                            onClick={reset}
                                            className="w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-5 text-base font-bold transition-all"
                                        >
                                            <CheckCircle2 className="w-5 h-5" />
                                            Download another file
                                        </button>
                                    )}

                                    <p className="mt-6 text-center text-xs text-slate-400">
                                        Powered by{" "}
                                        <a
                                            href="/"
                                            className="text-slate-600 hover:text-blue-600 font-semibold"
                                        >
                                            FilesCAD
                                        </a>{" "}
                                        &middot; Files served from <span className="font-mono">FilesCAD App</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        [STATUS.IDLE]: { label: "Ready", cls: "bg-slate-100 text-slate-600", Icon: KeyRound },
        [STATUS.INVALID]: { label: "Invalid", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
        [STATUS.DOWNLOADING]: { label: "Working", cls: "bg-blue-100 text-blue-700", Icon: Loader2 },
        [STATUS.SUCCESS]: { label: "Done", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
        [STATUS.FAILED]: { label: "Failed", cls: "bg-rose-100 text-rose-700", Icon: XCircle },
    };
    const { label, cls, Icon } = map[status];
    return (
        <span
            data-testid={`status-pill-${status}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${cls}`}
        >
            <Icon className={`w-3 h-3 ${status === STATUS.DOWNLOADING ? "animate-spin" : ""}`} />
            {label}
        </span>
    );
}
